import React, { useEffect, useState } from "react";
import { Plus, Play, Lock, X, Calendar } from "lucide-react";
import dayjs from "dayjs";

const API = process.env.REACT_APP_API_BASE || "http://127.0.0.1:8000";
const card = "rounded-xl border border-[#00ff90]/30 bg-[#0d0d0d] shadow-[0_0_12px_#00ff9022]";
const inputCls = "w-full bg-black border border-[#00ff90]/20 rounded-lg px-3 py-2 text-sm text-white outline-none focus:border-[#00ff90]/50 transition-colors";
const labelCls = "text-xs text-[#00ff90]/50 uppercase tracking-wider block mb-1.5";

const STATUS_STYLE = {
  DRAFT:       "text-white/40 bg-white/5 border-white/10",
  OPEN:        "text-[#00ff90] bg-[#00ff90]/10 border-[#00ff90]/20",
  REVIEW:      "text-yellow-400 bg-yellow-500/10 border-yellow-500/20",
  CALIBRATION: "text-blue-400 bg-blue-500/10 border-blue-500/20",
  CLOSED:      "text-white/25 bg-white/5 border-white/10",
};

const EMPTY = {
  name: "", description: "", profile_id: "",
  start_date: "", end_date: "", goal_setting_deadline: "",
  self_review_deadline: "", mgr_review_deadline: "",
};

export default function CyclesTab() {
  const [cycles,   setCycles]   = useState([]);
  const [profiles, setProfiles] = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [showNew,  setShowNew]  = useState(false);
  const [draft,    setDraft]    = useState(EMPTY);
  const [saving,   setSaving]   = useState(false);
  const [error,    setError]    = useState(null);
  const [actionBusy, setActionBusy] = useState(null);
  const token = localStorage.getItem("token");

  const load = async () => {
    setLoading(true);
    try {
      const [c, p] = await Promise.all([
        fetch(`${API}/api/admin/performance/cycles`,  { headers: { Authorization: `Bearer ${token}` } }).then(r => r.json()),
        fetch(`${API}/api/admin/performance/profiles`, { headers: { Authorization: `Bearer ${token}` } }).then(r => r.json()),
      ]);
      setCycles(Array.isArray(c) ? c : []);
      setProfiles(Array.isArray(p) ? p : []);
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const create = async () => {
    setSaving(true); setError(null);
    try {
      const res = await fetch(`${API}/api/admin/performance/cycles`, {
        method: "POST", headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify(draft),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setShowNew(false); setDraft(EMPTY); await load();
    } catch (e) { setError(e.message); }
    finally { setSaving(false); }
  };

  const cycleAction = async (id, action) => {
    setActionBusy(id + action);
    try {
      await fetch(`${API}/api/admin/performance/cycles/${id}/${action}`, {
        method: "POST", headers: { Authorization: `Bearer ${token}` },
      });
      await load();
    } catch (e) { setError(e.message); }
    finally { setActionBusy(null); }
  };

  const STATUS_ACTIONS = {
    DRAFT:       [{ key: "start",    label: "Start",             style: "border-[#00ff90]/25 bg-[#00ff90]/8 text-[#00ff90]/70 hover:bg-[#00ff90]/15 hover:text-[#00ff90]" }],
    OPEN:        [{ key: "lock",     label: "Lock for Calibration", style: "border-blue-500/25 bg-blue-500/8 text-blue-400/70 hover:bg-blue-500/15 hover:text-blue-400" }],
    CALIBRATION: [{ key: "close",   label: "Close Cycle",        style: "border-red-500/25 bg-red-500/8 text-red-400/70 hover:bg-red-500/15 hover:text-red-400" }],
  };

  return (
    <div className="space-y-5">

      {error && <div className="rounded-xl border border-red-500/30 bg-red-500/10 p-4 text-red-300 text-sm">⚠ {error}</div>}

      {/* New cycle form */}
      {showNew && (
        <div className={`${card} p-5 space-y-4`}>
          <p className="text-xs text-[#00ff90]/60 uppercase tracking-widest font-medium">New Performance Cycle</p>
          <div className="grid grid-cols-2 gap-3">
            <div><label className={labelCls}>Cycle Name</label>
              <input className={inputCls} value={draft.name} onChange={e => setDraft(d => ({...d, name: e.target.value}))} placeholder="e.g. FY2025 Annual Review" /></div>
            <div><label className={labelCls}>Performance Profile</label>
              <select className={inputCls} value={draft.profile_id} onChange={e => setDraft(d => ({...d, profile_id: e.target.value}))}>
                <option value="">Select profile…</option>
                {profiles.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select></div>
            <div><label className={labelCls}>Start Date</label>
              <input type="date" className={inputCls} value={draft.start_date} onChange={e => setDraft(d => ({...d, start_date: e.target.value}))} /></div>
            <div><label className={labelCls}>End Date</label>
              <input type="date" className={inputCls} value={draft.end_date} onChange={e => setDraft(d => ({...d, end_date: e.target.value}))} /></div>
            <div><label className={labelCls}>Goal Setting Deadline</label>
              <input type="date" className={inputCls} value={draft.goal_setting_deadline} onChange={e => setDraft(d => ({...d, goal_setting_deadline: e.target.value}))} /></div>
            <div><label className={labelCls}>Self Review Deadline</label>
              <input type="date" className={inputCls} value={draft.self_review_deadline} onChange={e => setDraft(d => ({...d, self_review_deadline: e.target.value}))} /></div>
            <div><label className={labelCls}>Manager Review Deadline</label>
              <input type="date" className={inputCls} value={draft.mgr_review_deadline} onChange={e => setDraft(d => ({...d, mgr_review_deadline: e.target.value}))} /></div>
            <div className="col-span-2"><label className={labelCls}>Description</label>
              <input className={inputCls} value={draft.description || ""} onChange={e => setDraft(d => ({...d, description: e.target.value}))} /></div>
          </div>
          <div className="flex gap-2 justify-end pt-2 border-t border-white/5">
            <button onClick={() => setShowNew(false)} className="text-xs px-4 py-2 rounded-lg border border-white/10 text-white/40 hover:text-white transition-colors">Cancel</button>
            <button onClick={create} disabled={saving || !draft.name}
              className="text-xs px-5 py-2 rounded-lg bg-[#00ff90]/15 text-[#00ff90] border border-[#00ff90]/30 hover:bg-[#00ff90]/25 disabled:opacity-40 transition-colors font-medium">
              {saving ? "Creating…" : "Create Cycle"}
            </button>
          </div>
        </div>
      )}

      {/* Cycles list */}
      <div className={card}>
        <div className="px-5 py-4 border-b border-white/5 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Calendar size={13} className="text-[#00ff90]/60" />
            <span className="text-xs text-[#00ff90]/60 uppercase tracking-widest font-medium">Performance Cycles</span>
          </div>
          <button onClick={() => setShowNew(v => !v)}
            className="flex items-center gap-1 text-xs px-3 py-1.5 rounded-lg border border-[#00ff90]/25 bg-[#00ff90]/8 text-[#00ff90]/70 hover:bg-[#00ff90]/15 hover:text-[#00ff90] transition-colors">
            <Plus size={12} /> New Cycle
          </button>
        </div>

        <div className="divide-y divide-white/5">
          {loading ? Array.from({length:3}).map((_,i) => <div key={i} className="px-5 py-4"><div className="h-16 rounded-lg bg-white/5 animate-pulse" /></div>)
          : cycles.length === 0 ? <p className="px-5 py-8 text-white/20 text-sm italic text-center">No cycles yet. Create one to start a review process.</p>
          : cycles.map(c => {
            const statusStyle = STATUS_STYLE[c.status] || STATUS_STYLE.DRAFT;
            const actions     = STATUS_ACTIONS[c.status] || [];
            const profile     = profiles.find(p => p.id === c.profile_id);

            return (
              <div key={c.id} className="flex items-start justify-between gap-3 px-5 py-4">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <p className="text-sm text-white/80 font-medium">{c.name}</p>
                    <span className={`text-xs px-2 py-0.5 rounded-full border ${statusStyle}`}>{c.status || "DRAFT"}</span>
                    {profile && (
                      <span className="text-xs text-[#00ff90]/50 border border-[#00ff90]/15 px-2 py-0.5 rounded-full">
                        {profile.name}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-3 flex-wrap text-xs text-white/30">
                    {c.start_date && <span>{dayjs(c.start_date).format("DD MMM YYYY")}</span>}
                    {c.end_date   && <span>→ {dayjs(c.end_date).format("DD MMM YYYY")}</span>}
                    {c.participant_count != null && <span>· {c.participant_count} participants</span>}
                  </div>
                  {c.description && <p className="text-xs text-white/20 mt-1">{c.description}</p>}
                </div>
                <div className="flex gap-2 shrink-0 flex-wrap">
                  {actions.map(({ key, label, style }) => (
                    <button key={key} onClick={() => cycleAction(c.id, key)}
                      disabled={actionBusy !== null}
                      className={`text-xs px-3 py-1.5 rounded-lg border disabled:opacity-40 transition-colors ${style}`}>
                      {actionBusy === c.id + key ? "…" : label}
                    </button>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}